// AIGC metadata default payload (inner object, without the outer {"AIGC": ...} wrapper)
export const DEFAULT_AIGC_METADATA = {
  Label: '1',
  ContentProducer: '15d25e34-2278-seede-b56f-8eca942f3e25',
  ProduceID: '15d25e34-2278-4dcd-b56f-8eca942f3e25',
  ReservedCode1: '2a1a37192a1a37192a1a37192a1a37192a1a3719',
  ContentPropagator: '15d25e34-2278-seede-b56f-8eca942f3e25',
  PropagateID: '15d25e34-2278-4dcd-b56f-8eca942f3e25',
  ReservedCode2: '',
};

export function createMetadata({
  Label = '1',
  ContentProducer,
  ProduceID,
  ContentPropagator,
  PropagateID,
  ReservedCode2 = '',
}) {
  // Defaults per spec note: on first write, propagator fields copy producer fields
  ContentProducer = ContentProducer || '15d25e34-2278-seede-b56f-8eca942f3e25';
  ProduceID = ProduceID || '15d25e34-2278-4dcd-b56f-8eca942f3e25';
  ContentPropagator = ContentPropagator || ContentProducer;
  PropagateID = PropagateID || ProduceID;

  // Calculate ReservedCode1 using a signature of core fields
  const data = `${Label}${ContentProducer}${ProduceID}${ContentPropagator}${PropagateID}`;
  const reservedCode1 = calculateSignature(data);

  const inner = {
    Label: String(Label || '1'),
    ContentProducer: String(ContentProducer || ''),
    ProduceID: String(ProduceID || ''),
    ReservedCode1: String(reservedCode1 || ''),
    ContentPropagator: String(ContentPropagator || ''),
    PropagateID: String(PropagateID || ''),
    ReservedCode2: String(ReservedCode2 || ''),
  };
  return sanitizeAIGCRecord(inner);
}

// Encryption signature algorithm to calculate ReserveCode1
function calculateSignature(data) {
  // Simple hash-based signature (in production, use proper cryptographic signing)
  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to hex string and pad to 40 characters
  const signature = Math.abs(hash).toString(16).padStart(8, '0');
  return signature.repeat(5).substring(0, 40);
}

// 验证签名的校验和函数
export function verifySignature(data, expectedSignature) {
  const calculatedSignature = calculateSignature(data);
  return calculatedSignature === expectedSignature;
}

// 验证AIGC元数据完整性
export function validateAIGCMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') {
    return { valid: false, error: 'Invalid metadata format' };
  }

  const required = [
    'Label',
    'ContentProducer',
    'ProduceID',
    'ReservedCode1',
    'ContentPropagator',
    'PropagateID',
  ];
  const missing = required.filter((field) => !metadata[field]);

  if (missing.length > 0) {
    return { valid: false, error: `Missing required fields: ${missing.join(', ')}` };
  }

  // 类型校验: 全为字符串
  for (const k of required.concat(['ReservedCode2'])) {
    if (typeof metadata[k] !== 'string') {
      return { valid: false, error: `Field ${k} must be a string` };
    }
  }

  // 值域校验: Label 只能为 '1' | '2' | '3'
  if (!['1', '2', '3'].includes(metadata.Label)) {
    return { valid: false, error: 'Label must be "1", "2" or "3"' };
  }

  // 允许字符集校验
  for (const [key, value] of Object.entries(metadata)) {
    const sanitized = sanitizeAIGCValue(String(value));
    if (sanitized !== value) {
      return { valid: false, error: `Field ${key} contains disallowed characters` };
    }
  }

  // 验证签名
  const { Label, ContentProducer, ProduceID, ContentPropagator, PropagateID, ReservedCode1 } =
    metadata;
  const data = `${Label}${ContentProducer}${ProduceID}${ContentPropagator}${PropagateID}`;
  const isSignatureValid = verifySignature(data, ReservedCode1);

  if (!isSignatureValid) {
    return { valid: false, error: 'Invalid signature checksum' };
  }

  return { valid: true };
}

// 计算数据校验和 (CRC32算法)
export function calculateChecksum(data) {
  const bytes = new TextEncoder().encode(data);
  let crc = 0xffffffff;

  for (let i = 0; i < bytes.length; i++) {
    crc = crc ^ bytes[i];
    for (let k = 0; k < 8; k++) {
      crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
    }
  }

  return ((crc ^ 0xffffffff) >>> 0).toString(16).padStart(8, '0');
}

// Allowed charset per spec: 0x21, 0x23-0x5B, 0x5D-0x7E and \" for quote
const allowedChar = (code) => {
  return code === 0x21 || (code >= 0x23 && code <= 0x5b) || (code >= 0x5d && code <= 0x7e);
};

export function sanitizeAIGCValue(value) {
  const s = String(value);
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    if (ch === 0x22) {
      // '"' -> escape
      out += '\\"';
    } else if (allowedChar(ch)) {
      out += s[i];
    } else {
      // drop disallowed
    }
  }
  return out;
}

export function sanitizeAIGCRecord(record) {
  const out = {};
  for (const [k, v] of Object.entries(record || {})) {
    out[k] = sanitizeAIGCValue(v);
  }
  // enforce Label domain after sanitize
  if (!['1', '2', '3'].includes(out.Label)) out.Label = '1';
  return out;
}

export function buildAIGCPayloadString(input) {
  const inner = input || DEFAULT_AIGC_METADATA;
  const clean = sanitizeAIGCRecord(inner);
  return JSON.stringify(clean);
}

function escapeXmlAttribute(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export function buildAIGCXMP(metadata) {
  const payload = buildAIGCPayloadString(metadata);
  const escaped = escapeXmlAttribute(payload);
  const xmp =
    `<?xpacket begin="\ufeff" id="W5M0MpCehiHzreSzNTczkc9d"?>\n` +
    `<x:xmpmeta xmlns:x="adobe:ns:meta/">\n` +
    `  <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">\n` +
    `    <rdf:Description xmlns:aigc="https://ns.seede.ai/aigc/1.0/" aigc:payload="${escaped}"/>\n` +
    `  </rdf:RDF>\n` +
    `</x:xmpmeta>\n` +
    `<?xpacket end="w"?>`;
  return new TextEncoder().encode(xmp);
}

export async function injectXMPIntoJPEG(jpegBlob, xmpBytes) {
  const buffer = await jpegBlob.arrayBuffer();
  const src = new Uint8Array(buffer);
  // JPEG SOI check
  if (!(src[0] === 0xff && src[1] === 0xd8)) return jpegBlob;

  const headerStr = 'http://ns.adobe.com/xap/1.0/\x00';
  const headerBytes = new TextEncoder().encode(headerStr);
  const app1Payload = new Uint8Array(headerBytes.length + xmpBytes.length);
  app1Payload.set(headerBytes, 0);
  app1Payload.set(xmpBytes, headerBytes.length);

  const app1Len = app1Payload.length + 2; // includes the two length bytes
  const app1 = new Uint8Array(2 + 2 + app1Payload.length);
  app1[0] = 0xff;
  app1[1] = 0xe1; // APP1 marker
  app1[2] = (app1Len >> 8) & 0xff;
  app1[3] = app1Len & 0xff;
  app1.set(app1Payload, 4);

  const out = new Uint8Array(src.length + app1.length);
  // SOI
  out[0] = src[0];
  out[1] = src[1];
  // APP1 after SOI
  out.set(app1, 2);
  // Rest of JPEG
  out.set(src.subarray(2), 2 + app1.length);

  return new Blob([out], { type: 'image/jpeg' });
}

export async function injectXMPIntoWebP(webpBlob, xmpBytes) {
  const buffer = await webpBlob.arrayBuffer();
  const src = new Uint8Array(buffer);
  const len = src.length;
  // RIFF WEBP check
  const isRIFF = src[0] === 0x52 && src[1] === 0x49 && src[2] === 0x46 && src[3] === 0x46;
  const isWEBP = src[8] === 0x57 && src[9] === 0x45 && src[10] === 0x42 && src[11] === 0x50;
  if (!isRIFF || !isWEBP) return webpBlob;

  function readUint32LE(o) {
    return (src[o] | (src[o + 1] << 8) | (src[o + 2] << 16) | (src[o + 3] << 24)) >>> 0;
  }

  // Walk chunks to find VP8X, image chunk, or existing XMP
  let offset = 12;
  let vp8xOffset = -1;
  let vp8xSize = 0;
  let vp8xFlagsOffset = -1;
  let firstImageChunkOffset = -1;
  let xmpChunkStart = -1;
  let xmpChunkSize = 0;

  while (offset + 8 <= len) {
    const c0 = src[offset];
    const c1 = src[offset + 1];
    const c2 = src[offset + 2];
    const c3 = src[offset + 3];
    const size = readUint32LE(offset + 4);
    const dataStart = offset + 8;
    const dataEnd = dataStart + size;
    if (dataEnd > len) break;
    const rounded = size + (size % 2);
    const fourCC = String.fromCharCode(c0, c1, c2, c3);

    if (fourCC === 'VP8X') {
      vp8xOffset = offset;
      vp8xSize = size;
      vp8xFlagsOffset = dataStart; // first byte in VP8X payload are feature flags
    }
    if (
      (fourCC === 'VP8 ' || fourCC === 'VP8L' || fourCC === 'ANIM') &&
      firstImageChunkOffset === -1
    ) {
      firstImageChunkOffset = offset;
    }
    if (fourCC === 'XMP ') {
      xmpChunkStart = offset;
      xmpChunkSize = size;
      break;
    }

    offset = dataStart + rounded;
  }

  const xmpLen = xmpBytes.length;
  const xmpPadded = xmpLen % 2 ? xmpLen + 1 : xmpLen;

  function writeUint32LE(view, o, value) {
    view[o] = value & 0xff;
    view[o + 1] = (value >> 8) & 0xff;
    view[o + 2] = (value >> 16) & 0xff;
    view[o + 3] = (value >> 24) & 0xff;
  }

  // If XMP chunk exists, replace it
  if (xmpChunkStart !== -1) {
    const oldPadded = xmpChunkSize % 2 ? xmpChunkSize + 1 : xmpChunkSize;
    const delta = 8 + xmpPadded - (8 + oldPadded);
    const out = new Uint8Array(src.length + delta);
    // Copy before XMP
    out.set(src.subarray(0, xmpChunkStart), 0);
    let o = xmpChunkStart;
    // Write XMP header
    out[o++] = 0x58;
    out[o++] = 0x4d;
    out[o++] = 0x50;
    out[o++] = 0x20; // 'XMP '
    writeUint32LE(out, o, xmpLen);
    o += 4;
    // Data
    out.set(xmpBytes, o);
    o += xmpLen;
    if (xmpPadded !== xmpLen) out[o++] = 0;
    // Copy rest (skip old XMP)
    const afterOld = xmpChunkStart + 8 + oldPadded;
    out.set(src.subarray(afterOld), o);
    // Update RIFF size
    const oldSize = readUint32LE(4);
    const newSize = (oldSize + delta) >>> 0;
    writeUint32LE(out, 4, newSize);
    // Ensure VP8X flag for XMP is set, if present
    if (vp8xFlagsOffset !== -1) {
      out[vp8xFlagsOffset] = out[vp8xFlagsOffset] | 0x08;
    }
    return new Blob([out], { type: 'image/webp' });
  }

  // Choose insertion point: after VP8X if present, else after first chunk
  let insertAt = -1;
  if (vp8xOffset !== -1) {
    const rounded = vp8xSize + (vp8xSize % 2);
    insertAt = vp8xOffset + 8 + rounded;
  } else if (firstImageChunkOffset !== -1) {
    const firstSize = readUint32LE(firstImageChunkOffset + 4);
    const rounded = firstSize + (firstSize % 2);
    insertAt = firstImageChunkOffset + 8 + rounded;
  } else {
    insertAt = len;
  }

  const deltaAdd = 8 + xmpPadded;
  const out = new Uint8Array(src.length + deltaAdd);
  // Copy up to insertion point
  out.set(src.subarray(0, insertAt), 0);
  let o = insertAt;
  // Write XMP chunk
  out[o++] = 0x58;
  out[o++] = 0x4d;
  out[o++] = 0x50;
  out[o++] = 0x20; // 'XMP '
  writeUint32LE(out, o, xmpLen);
  o += 4;
  out.set(xmpBytes, o);
  o += xmpLen;
  if (xmpPadded !== xmpLen) out[o++] = 0;
  // Copy remainder
  out.set(src.subarray(insertAt), o);
  // Update RIFF size
  const oldSize = readUint32LE(4);
  const newSize = (oldSize + deltaAdd) >>> 0;
  writeUint32LE(out, 4, newSize);
  // Ensure VP8X XMP flag is set if VP8X exists
  if (vp8xFlagsOffset !== -1) {
    out[vp8xFlagsOffset] = out[vp8xFlagsOffset] | 0x08;
  }
  return new Blob([out], { type: 'image/webp' });
}
